import * as React from 'react';

import { connect } from 'react-redux';
import { AnyAction } from 'redux';
import { Field, formValueSelector } from 'redux-form';
import { ThunkDispatch } from 'redux-thunk';

import Button from '@material-ui/core/Button';
import grey from '@material-ui/core/colors/grey';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import { withStyles, createStyles, WithStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';

import type { JobFieldProps } from './job-field';

import { updateOrg } from '../../action/org/update-org';

import { normalizePhone } from '../../businesslogic/form-normalizers';
import { validatePhone } from '../../businesslogic/form-validators';

import { JobField } from './job-field';

import { formatPhoneNumber } from '../../util/formatters';
import { ICON_NAMES, MaterialDesignIcon } from '../../util/material-design-icons';
import { TextField } from '../../widgets/redux-form-fields/material-ui-wrappers';
import {
  EditingStateDefaultNumberMessage,
  DefaultNumberMessage,
} from '../widgets/default-number-message';

const styles = () =>
  createStyles({
    field: {
      margin: '8px',
      width: '80%',
    },
    setAsDefaultButton: {
      minHeight: '0px',
      paddingBottom: '2px',
      paddingTop: '2px',
      marginTop: '5px',
      float: 'left',
      fontSize: '0.7rem',
      color: grey[500],
    },
    useDefaultsButton: {
      minHeight: '0px',
      paddingBottom: '2px',
      paddingTop: '2px',
      marginTop: '5px',
      float: 'left',
      fontSize: '0.7rem',
      color: grey[500],
    },
  });

const mapStateToProps = (state: any, ownProps: InputProps) => {
  let selector = null;
  if (ownProps.defaultButtonsConfiguration) {
    selector = formValueSelector(ownProps.defaultButtonsConfiguration.formName);
  }
  return {
    org: state.orgs.orgsById && ownProps.job.orgId ? state.orgs.orgsById[ownProps.job.orgId] : null,
    forwardingPhoneNumber: selector ? selector(state, 'forwardingPhoneNumber') : null,
    forwardingPhoneNumberForCalls: selector
      ? selector(state, 'forwardingPhoneNumberForCalls')
      : null,
  };
};

const mapDispatchToProps = (
  dispatch: ThunkDispatch<any, any, AnyAction>,
  ownProps: InputProps,
) => ({
  actions: {
    updateOrg: (update: Partial<Org>) =>
      ownProps.job.orgId ? dispatch(updateOrg(ownProps.job.orgId, update)) : undefined,
  },
});

interface InputProps {
  job: Partial<Job>;
  defaultButtonsConfiguration?: {
    change: Function;
    formName: string;
  };
}

interface Props
  extends InputProps,
    JobFieldProps,
    WithStyles<typeof styles>,
    ReturnType<typeof mapStateToProps>,
    ReturnType<typeof mapDispatchToProps> {}
type ChildrenProps = Partial<Props>;

const JobFieldContactInfoComponent = (props: Props) => {
  const {
    defaultButtonsConfiguration,
    forwardingPhoneNumber,
    forwardingPhoneNumberForCalls,
    actions,
    classes,
    job,
  } = props;
  const org = props.org || job.org;

  const useDefaults = () => {
    if (!org || !defaultButtonsConfiguration) {
      return;
    }
    defaultButtonsConfiguration.change(
      'forwardingPhoneNumber',
      org.forwardingPhoneNumber?.replace('+1', '') || null,
    );
    defaultButtonsConfiguration.change(
      'forwardingPhoneNumberForCalls',
      org.jobForwardingPhoneNumber?.replace('+1', '') || null,
    );
  };

  const updateDefaults = () => {
    if (!defaultButtonsConfiguration) {
      return;
    }

    actions.updateOrg({
      forwardingPhoneNumber,
      jobForwardingPhoneNumber: `+1${forwardingPhoneNumberForCalls.replace(/[^0-9]/g, '')}`,
    });
  };

  const childrenProps: ChildrenProps = { ...props };
  delete childrenProps.classes;
  const hasJobPhoneNumber = !!job.fromPhoneNumber;
  const caption = hasJobPhoneNumber
    ? 'Set up where applicant phone calls and messages forward to.'
    : 'Set up where applicant phone calls for this job forward to.';

  const phoneNumberField = (
    fieldName: string,
    labelText: string,
    className: string,
    value?: string | null,
  ) => (
    <Grid item xs={12} sm={6}>
      <Field
        className={className}
        name={fieldName}
        label={`Phone # For ${labelText}`}
        normalize={normalizePhone}
        validate={[validatePhone]}
        component={TextField}
        value={value}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              {labelText === 'Calls' ? (
                <MaterialDesignIcon iconName={ICON_NAMES.CALL} />
              ) : (
                <MaterialDesignIcon iconName={ICON_NAMES.SMS} />
              )}
            </InputAdornment>
          ),
          placeholder: '123-456-7890',
        }}
      />
    </Grid>
  );

  return (
    <JobField
      {...childrenProps}
      title="Contact Information"
      caption={caption}
      editView={
        <Grid container alignItems="flex-start">
          {defaultButtonsConfiguration && hasJobPhoneNumber ? (
            <Grid item xs={12}>
              <Grid item>
                <Button onClick={useDefaults} className={classes.useDefaultsButton}>
                  Use Company Defaults
                </Button>
              </Grid>
              <Grid item>
                <Button onClick={updateDefaults} className={classes.setAsDefaultButton}>
                  Set as Company Defaults
                </Button>
              </Grid>
            </Grid>
          ) : null}
          {!hasJobPhoneNumber && <EditingStateDefaultNumberMessage />}
          {phoneNumberField(
            'forwardingPhoneNumberForCalls',
            'Calls',
            classes.field,
            job.forwardingPhoneNumberForCalls,
          )}
          {hasJobPhoneNumber &&
            phoneNumberField(
              'forwardingPhoneNumber',
              'Text Messages',
              classes.field,
              job.forwardingPhoneNumber,
            )}
        </Grid>
      }
      displayView={
        <>
          <DefaultNumberMessage org={org} job={job} />
          <Grid container alignItems="flex-start" className="u-margin-y">
            <Grid item container alignItems="flex-start" xs={12} sm={6} spacing={1}>
              <Grid item>
                <MaterialDesignIcon iconName={ICON_NAMES.CALL} />
              </Grid>
              <Grid item>
                <Typography variant="body2">
                  {formatPhoneNumber(job.forwardingPhoneNumberForCalls) ||
                    formatPhoneNumber(org?.jobForwardingPhoneNumber) ||
                    'No Forwarding Number'}
                </Typography>
              </Grid>
            </Grid>
            {hasJobPhoneNumber && (
              <Grid item container alignItems="flex-start" xs={12} sm={6} spacing={1}>
                <Grid item>
                  <MaterialDesignIcon iconName={ICON_NAMES.SMS} />
                </Grid>
                <Grid item>
                  <Typography variant="body2">
                    {formatPhoneNumber(job.forwardingPhoneNumber) || 'No Forwarding Number'}
                  </Typography>
                </Grid>
              </Grid>
            )}
          </Grid>
        </>
      }
    />
  );
};

export const JobFieldContactInfo = connect(
  mapStateToProps,
  mapDispatchToProps,
)(withStyles(styles)(JobFieldContactInfoComponent));
